<template>
  <div v-if="recentAccessEntries && currentUser">
    <div class="profile-header">{{ userDetails.lastName }}, {{ userDetails.firstName }}</div>
    <CreatedComment :comment="userDetails.created_comment" />
    <div class="profile-body">
      <div class="row">
        <div class="profile-panel">
          <div>
            <!--container div for wrapping updateable section of user profile, ie. roles and is-active flag -->
            <div class="title">{{ $t('profile.titles.userDetails') }}</div>
            <div v-if="!userDetails.is_active" class="inactive">
              <font-awesome-icon icon="exclamation-triangle" />
              {{ $t('profile.labels.inactiveUser') }}
            </div>
            <div class="profile-entry profile-username-container">
              <span class="profile-label"
                ><font-awesome-icon v-if="userDetails.is_active" icon="user" />
                <font-awesome-icon v-else icon="user-slash" />
                {{ $t('profile.labels.userName') }}:</span
              >
              <div v-if="isUserAdmin" class="profile-username-input-container">
                <InputField
                  v-model="username"
                  :menu-label="username"
                  name="username"
                  class="profile-username-input"
                  @input="setUsername"
                />
                <font-awesome-icon
                  v-if="username.length > 2 && isUsernameTaken === false"
                  icon="check-circle"
                  class="profile-username-icon-success"
                />
                <font-awesome-icon
                  v-if="username.length > 2 && isUsernameTaken && username !== userDetails.username"
                  icon="circle-xmark"
                  class="profile-username-icon-error"
                />
              </div>
              <span v-else :class="{ strike: !userDetails.is_active }">
                {{ userDetails.username }}
              </span>
              <span v-if="canImpersonate" class="link" @click="doImpersonate()">
                <font-awesome-icon icon="user-secret" />{{ $t('profile.labels.impersonate') }}
              </span>
            </div>
            <div class="profile-entry">
              <span class="profile-label">
                <font-awesome-icon icon="lock-open" /> {{ $t('profile.labels.roles') }}:</span
              >

              <div v-for="(role, index) in roles" :key="index">
                {{ role.role_name }}
              </div>
            </div>
            <div class="profile-entry">
              <span class="profile-label">
                <font-awesome-icon icon="envelope" /> {{ $t('profile.labels.email') }}:</span
              >
              {{ userDetails.email }}
              <span v-if="userDetails.email_alt">
                {{ '(' + userDetails.email_alt + ' - ' + $t('profile.labels.altEmail') + ')' }}
              </span>
            </div>

            <div v-if="isUserAdmin || isBranchAdmin" class="profile-entry">
              <span class="profile-label">
                <font-awesome-icon icon="envelope" /> {{ $t('profile.labels.password') }}:</span
              >
              <span
                v-if="!changeInstructionSent"
                class="link"
                @click="doSendChangeInstructions()"
                >{{ $t('profile.labels.sendChangeInstructions') }}</span
              >
              <span v-else class="sent">
                {{ $t('profile.labels.changeSent') }}
                <font-awesome-icon icon="check-circle" />
              </span>
            </div>

            <div class="profile-entry">
              <span class="profile-label">
                <font-awesome-icon icon="phone-square" /> {{ $t('profile.labels.phones') }}:</span
              >
              {{ userDetails.phone1 }}

              <span v-if="userDetails.phone2"> {{ ', ' + userDetails.phone2 }} </span>
              <span v-if="userDetails.phone3">
                {{ ', ' + userDetails.phone3 }} {{ userDetails.phone3_type }}</span
              >
            </div>
            <div v-if="isUserTesEmployee" class="profile-entry">
              <span class="profile-label">
                <font-awesome-icon icon="building" /> {{ $t('profile.labels.branch') }}:</span
              >
              {{ userDetails.branchName }}
            </div>
            <div v-if="userDetails.payrollnums" class="profile-entry">
              <span class="profile-label">{{ $t('profile.labels.payroll') }}:</span>
              {{ userDetails.payrollnums }}
            </div>
            <div v-if="userDetails.pay_to_name" class="profile-entry">
              <span class="profile-label">{{ $t('profile.labels.payto') }}:</span>
              {{ userDetails.pay_to_name }}
            </div>
            <div v-if="userDetails.entity" class="profile-entry">
              <span class="profile-label">{{ $t('profile.labels.entity') }}:</span>
              {{ userDetails.entity }}
            </div>
            <div v-if="isUserAdmin" class="profile-entry">
              <span class="profile-label">
                <font-awesome-icon icon="bell" /> {{ $t('profile.labels.notifications') }}:</span
              >
              <span class="user-notifications-count">{{ getUserUnreadNotificationCount }}</span>
              <router-link :to="notificationsLink">view</router-link>
            </div>
            <div v-if="isUserAdmin" class="profile-entry">
              <span class="profile-label">
                <font-awesome-icon icon="cogs" /> {{ $t('profile.labels.preferences') }}:</span
              >
              <router-link :to="preferencesLink">view</router-link>
            </div>

            <div v-if="canView" class="profile-entry">
              <span class="profile-label">
                <label> {{ $t('users.labels.roles') }} </label>
              </span>
              <div class="role-container">
                <tol-multi-select
                  :item-list="
                    userDetails.allowedRoles ? userDetails.allowedRoles.map(r => r.role_name) : []
                  "
                  :current-items="roles.map(r => r.role_name)"
                  @item-change="addRemoveRole($event)"
                >
                </tol-multi-select>
              </div>
            </div>
            <div class="profile-entry active">
              <div>Active</div>
              <div>
                <Checkbox
                  size="small"
                  :checked="userDetails.is_active"
                  @change="onToggleActive($event)"
                />
              </div>
            </div>
          </div>

          <div v-if="canView" class="module-action-container">
            <!--
            <tol-alt-button label="Save" :disabled="!isOnScreenDataModified" @click="updateUserProfile()">
            </tol-alt-button>
            -->
            <tol-button
              id="save-btn"
              button-type="save"
              label="Save"
              :class="['module-action', { disabled: !isOnScreenDataModified }]"
              @click="updateUserProfile()"
            >
            </tol-button>
          </div>
        </div>
        <!-- end user details panel -->
        <div class="profile-panel">
          <div class="title">{{ $t('profile.titles.recentActions') }}</div>
          <tol-table
            v-if="recentActionsEntries"
            class="profile"
            :table-data="recentActionsEntries"
            :columns="actionColumns"
          >
            <template v-slot:tableHeader>
              <th>{{ $t('profile.titles.timestamp') }}</th>
              <th>{{ $t('profile.titles.item') }}</th>
              <th>{{ $t('profile.titles.action') }}</th>
            </template>
            <template v-slot:initiatedAt="{ value }" class="profile">
              {{ value.initiatedAt | formatDateTime }}
            </template>
            <template v-slot:objectType="{ value }">
              <router-link :to="`${value.description.urlPart}/${value.objectReferenceNumber}`">
                <font-awesome-icon :icon="value.description.icon" />
                {{ value.description.label }}
              </router-link>
            </template>
            <template v-slot:action="{ value }">
              {{ value.action | readable | capitalize }}
            </template>
          </tol-table>
          <br /><br />
          <div class="title">{{ $t('profile.titles.recentlyViewed') }}</div>
          <tol-table
            v-if="recentViewEntries"
            class="profile"
            :table-data="recentViewEntries"
            :columns="viewColumns"
          >
            <template v-slot:tableHeader>
              <th>{{ $t('profile.titles.timestamp') }}</th>
              <th>{{ $t('profile.titles.item') }}</th>
            </template>
            <template v-slot:initiatedAt="{ value }" class="profile">
              {{ value.initiatedAt | formatDateTime }}
            </template>
            <template v-slot:objectType="{ value }">
              <td>
                <router-link :to="`${value.description.urlPart}/${value.objectReferenceNumber}`">
                  <font-awesome-icon :icon="value.description.icon" />
                  {{ value.description.label }}
                </router-link>
              </td>
            </template>
          </tol-table>
        </div>
        <!--end user recent activity panel -->
      </div>
      <div class="row">
        <div class="profile-panel">
          <div class="title">{{ $t('profile.titles.recentlyAccessed') }}</div>
          <tol-table
            v-if="recentAccessEntries"
            class="profile"
            :table-data="recentAccessEntries"
            :columns="accessColumns"
          >
            <template v-slot:tableHeader>
              <th>{{ $t('profile.titles.timestamp') }}</th>
              <th>{{ $t('profile.titles.type') }}</th>
              <th>{{ $t('profile.titles.ipAddress') }}</th>
            </template>
            <template v-slot:initiatedAt="{ value }" class="profile">
              {{ value.initiatedAt | formatDateTime }}
            </template>
            <template v-slot:action="{ value }">
              <td>
                {{ value.action | readable | capitalize }}
              </td>
            </template>
          </tol-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import i18n from '@/i18n';
import Checkbox from '@/app/components/Checkbox';
import CreatedComment from '@/app/components/CreatedComment';
import InputField from '@/app/components/InputField';
import TolButton from '@/app/components/TolButton';
import TolMultiSelect from '@/app/components/TolMultiSelect';
import TolTable from '@/app/components/TolTable';
import { AppActions } from '@/app/app.keys';
import { userUtils } from '@/mixins/user-utils';
import { urlUtils } from '@/mixins/url-utils';
import { ProfileActions, ProfileGetters } from './profile-listing.keys';
import {
  NotificationsListingActions,
  NotificationsListingGetters
} from '@/app/features/notifications/features/notifications-listing/notifications-listing.keys';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';

export default {
  components: { Checkbox, CreatedComment, InputField, TolButton, TolMultiSelect, TolTable },
  mixins: [urlUtils, userUtils],
  data() {
    return {
      accessColumns: ['initiatedAt', 'action', 'ipAddress'],
      actionColumns: ['initiatedAt', 'objectType', 'action'],
      viewColumns: ['initiatedAt', 'objectType'],
      isOnScreenDataModified: false,
      username: '',
      isUsernameTaken: null,
      roles: [],
      userUnreadNotificationsCount: null,
      changeInstructionSent: false
    };
  },
  computed: {
    getUserUnreadNotificationCount() {
      return this.$store.getters[NotificationsListingGetters.ProfileUnreadNotifications];
    },
    currentProfile() {
      return this.$store.getters[ProfileGetters.GetCurrentProfile];
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    canImpersonate() {
      return (
        this.currentUser &&
        this.currentUser.roles.map(r => r.role_name).includes('System Admin') &&
        this.currentUser.id !== this.userDetails.id
      );
    },
    isUserTesEmployee() {
      return this.currentUser.roles.some(role => {
        if (role.role_name == 'TES Employee') return true;
      });
    },
    allowedRoles() {
      return this.userDetails &&
        Object.keys(this.userDetails).length !== 0 &&
        this.userDetails.allowedRoles
        ? this.userDetails.allowedRoles
        : [];
    },
    preferencesLink() {
      return this.$route.params.id
        ? `/preferences/${this.userDetails.reference_number}`
        : '/preferences';
    },
    notificationsLink() {
      return this.$route.params.id
        ? `/notifications/listing/${this.userDetails.reference_number}`
        : '/notifications/listing';
    },
    recentAccessEntries() {
      return this.$store.getters[ProfileGetters.GetRecentAccessEntries];
    },
    recentActionsEntries() {
      return this.$store.getters[ProfileGetters.GetRecentActionsEntries];
    },
    recentViewEntries() {
      return this.$store.getters[ProfileGetters.GetRecentViewEntries];
    },
    isUserAdmin() {
      return this.currentUser.roles.some(el => el.role_name == 'System Admin');
    },
    isBranchAdmin() {
      return this.currentUser.roles.some(el => el.role_name == 'Branch Admin');
    },
    canView() {
      return (
        !!this.currentUser &&
        !!this.currentUser.permissions['USER_ROLES'] &&
        !!this.currentUser.permissions['USER_ROLES']['EDIT']
      );
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(val) {
        if (val.params.id) {
          this.$store.dispatch(ProfileActions.UpdateUserData, val.params.id);
        }
        this.$store.dispatch(
          NotificationsListingActions.RetrieveNotificationsByReferenceNumber,
          val.params.id
        );
      }
    },
    userDetails: {
      immediate: true,
      handler(val) {
        if (val) {
          if (val.username) {
            this.username = val.username;
          }
          if (val.roles) {
            this.roles = val.roles.slice();
          }
        }
      }
    },
    isUsernameTaken: {
      immediate: false,
      handler(val) {
        if (val) {
          this.isOnScreenDataModified = false;
        } else {
          this.isOnScreenDataModified = true;
        }
      }
    }
  },
  mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, this.$t('profile.titles.title'));
    //the route parameter is the user's reference number, NOT their id
    let user_reference_number = this.$route.params.id;

    if (this.$route.params.id) {
      this.$store.dispatch(ProfileActions.UpdateUserData, this.$route.params.id);
    }
    this.$store.dispatch(
      NotificationsListingActions.RetrieveNotificationsByReferenceNumber,
      user_reference_number
    );
  },
  methods: {
    updateUserProfile() {
      if (this.username.length === 0) {
        this.$store.dispatch(TolErrorsActions.SetError, {
          message: i18n.t('profile.errors.usernameBlankError')
        });
        return;
      }
      if (this.username.length < 3) {
        this.$store.dispatch(TolErrorsActions.SetError, {
          message: i18n.t('profile.errors.usernameTooShort')
        });
        return;
      }
      this.$store.dispatch(ProfileActions.SaveUserDetails, {
        currentUserId: this.currentUser.id,
        username: this.username,
        userRoles: this.roles,
        isActive: this.userDetails.is_active,
        id: this.userDetails.id
      });
    },
    async setUsername() {
      if (this.userDetails.username === this.username) {
        this.isUsernameTaken = true;
        return;
      }

      // Check if the requested username is taken if more than 2 characters are entered
      if (this.username.length > 2) {
        let timer;
        clearTimeout(timer);
        timer = setTimeout(async () => {
          this.isUsernameTaken = await this.$store.dispatch(
            ProfileActions.CheckIfUsernameIsTaken,
            this.username
          );
        }, 2000);
      }
    },
    addRemoveRole(evt) {
      this.roles = this.allowedRoles.filter(r => evt.includes(r.role_name));
      this.isOnScreenDataModified = true;
    },
    doImpersonate() {
      this.$store.dispatch(ProfileActions.ImpersonateUser, this.userDetails.username);
    },
    onToggleActive(event) {
      this.isOnScreenDataModified = true;
      this.$store.dispatch(ProfileActions.ToggleUserActive, event.checked);
    },
    async doSendChangeInstructions() {
      const res = await this.$store.dispatch(ProfileActions.SendChangeInstructions, {
        username: this.userDetails.username,
        email: this.userDetails.email
      });
      if (res && res.pwResetCode) {
        this.changeInstructionSent = true;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/app/styles/colors.scss';
@import '@/app/styles/action-buttons.scss';

.user-notifications-count {
  // This color was taken for v3
  background-color: #3a87ad;
  border-radius: 8px;
  color: white;
  margin: 0 5px 0 5px;
  padding: 0 5px 0 5px;
}

.profile-header {
  background-color: $background-level-2;
  padding: 60px;
  font-size: 1.5rem;
  font-weight: bold;
}

.profile-username-container {
  display: flex;
  align-items: center;

  .profile-label,
  .link {
    display: flex;

    svg {
      margin-right: 0.25rem;
    }
  }
}

.profile-username-input-container {
  display: flex;
  align-items: center;

  .profile-username-input {
    margin-right: 0.5rem;
  }
}

.profile-username-input {
  border: 2px solid $row-border;
}

.profile-username-icon-success {
  color: green;
}

.profile-username-icon-error {
  color: red;
}

.profile-body {
  display: flex;
  flex-direction: column;
  padding: 60px 0;

  margin-top: 15px;

  .profile-entry {
    padding-bottom: 10px;
    .profile-label {
      font-weight: bold;
      padding-right: 0.5rem;
    }
    div {
      padding: 0.2rem;
    }

    .strike {
      text-decoration: line-through;
      color: $red;
    }

    .role-container {
      border: 2px solid $row-border;
      min-height: 240px;
    }

    .link {
      &:hover {
        cursor: pointer;
        color: orange;
      }
    }
    .strike,
    .link {
      padding: 0 5px;
    }
    &.active {
      display: flex;
      align-items: center;
    }
    .sent {
      color: $green;
    }
  }
  .row {
    display: flex;
    flex-direction: column;
    .profile-panel {
      padding: 60px;
      background-color: $background-level-2;
      margin-bottom: 15px;
      flex-basis: 50%;
      .title {
        border-bottom: 2px solid $row-border;
        margin-bottom: 12px;
      }
      .inactive {
        color: $red;
        padding: 12px 8px;
        border: 1px solid $red;
        background-color: $secondary-error;
        margin-bottom: 50px;
      }
      .module-action-container {
        margin-top: 20px;
        padding: 0;
        padding-top: 12px;
        background-color: $white;
        display: flex;
        justify-content: flex-end;
        .tol-button {
          margin: 0;
        }
      }
    }
  }
}

@media only screen and (min-width: 768px) {
  .profile-body {
    .row {
      flex-direction: row;
    }
  }
}
</style>
